import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import {
  LayoutDashboard, Users, Image, Package, Car, MapPin, GalleryHorizontal,
  MessageSquareQuote, HelpCircle, Settings, LogOut, UserCircle, Activity,
  Home as HomeIcon, Layers, Sparkles, Flame,
} from "lucide-react";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Leads", icon: Users },
  { to: "/admin/hero", label: "Hero Slider", icon: Image },
  { to: "/admin/homepage", label: "Homepage CMS", icon: HomeIcon },
  { to: "/admin/packages", label: "Tour Packages", icon: Package },
  { to: "/admin/services", label: "Services", icon: Sparkles },
  { to: "/admin/vehicle-categories", label: "Vehicle Categories", icon: Layers },
  { to: "/admin/vehicles", label: "Vehicles", icon: Car },
  { to: "/admin/destinations", label: "Destinations", icon: MapPin },
  { to: "/admin/gallery", label: "Gallery", icon: GalleryHorizontal },
  { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle },
  { to: "/admin/trending-popup", label: "Trending Popup", icon: Flame },
  { to: "/admin/settings", label: "Website Settings", icon: Settings },
  { to: "/admin/profile", label: "Profile", icon: UserCircle },
  { to: "/admin/activity", label: "Activity Logs", icon: Activity },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen grid grid-cols-[260px_1fr] bg-slate-100 font-body">
      <aside className="bg-brand-navy-950 text-slate-200 h-screen sticky top-0 flex flex-col">
        <div className="px-6 py-6 border-b border-white/10">
          <div className="font-display text-xl text-white">{settings?.company_name || "Vizag Jagan Travels"}</div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-amber-400 mt-1">Admin Console</div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`sidebar-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-white text-slate-950 font-medium" : "text-slate-300 hover:bg-white/5"
                }`
              }
            >
              <l.icon className="w-4 h-4" />
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <button
            onClick={doLogout}
            data-testid="admin-logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-white/5"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      <div className="min-h-screen">
        <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="px-8 h-14 flex items-center justify-between">
            <div className="text-sm text-slate-500">Signed in as <span className="text-slate-900 font-medium">{user?.email}</span></div>
            <a href="/" target="_blank" rel="noreferrer" className="text-sm text-slate-600 hover:text-slate-900" data-testid="view-site-link">View live site →</a>
          </div>
        </div>
        <div className="p-8">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
}
